import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, Plus, AlertCircle, ChevronRight,
  Clock, TrendingUp,
} from "lucide-react";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { VehicleCard, VehicleCardSkeleton } from "../components/VehicleCard";
import { HealthScoreBadge } from "../components/HealthScoreBadge";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { Button } from "../components/UIKit";
import { mapNetworkError, formatDate, statusColors, RULES_LABELS } from "../lib/fleet";

export default function HomeV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/home");
      setData(d);
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayName = (user?.name || "").trim();
  const firstName = displayName ? displayName.split(" ")[0] : "";
  const greeting = firstName ? `Bonjour ${firstName} 👋` : "Bienvenue";
  const hasVehicles = (data?.counters?.total_vehicles ?? 0) > 0;

  if (loading) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <PageHeader title="Accueil" subtitle={greeting} />
        <div style={{ padding: "0 20px 60px" }}>
          <div style={{ height: 180, background: "var(--mf-bg-muted)", borderRadius: 24, marginBottom: 20 }} />
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 14,
          }}>
            <VehicleCardSkeleton />
            <VehicleCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (errorMsg && !data) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <PageHeader title="Accueil" subtitle={greeting} />
        <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(); }} testID="home-error" />
      </div>
    );
  }

  if (!hasVehicles) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <PageHeader title="Accueil" subtitle={greeting} />
        <EmptyState
          testID="home-empty"
          icon={<Car size={64} color="var(--mf-border)" />}
          title="Commencez votre flotte"
          subtitle="Ajoutez votre premier véhicule pour suivre ses entretiens, documents et alertes."
          action={
            <Button
              testID="home-empty-add"
              icon={<Plus size={18} />}
              onClick={() => navigate("/vehicles/new")}
              fullWidth={false}
              style={{ paddingLeft: 22, paddingRight: 22 }}
            >
              Ajouter un véhicule
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }} data-testid="home-v2">
      <PageHeader title="Accueil" subtitle={greeting} />

      <div style={{ padding: "0 20px 100px", display: "flex", flexDirection: "column", gap: 24 }}>
        {data?.global_score ? (
          <FleetScoreHero
            score={data.global_score}
            counters={data.counters}
            onDashboard={() => navigate("/dashboard")}
          />
        ) : null}

        {data.urgent_alerts && data.urgent_alerts.length > 0 && (
          <UrgentSection
            alerts={data.urgent_alerts}
            total={data.urgent_alerts_total}
            onSeeAll={() => navigate("/alerts")}
            onAlert={(a) => navigate(`/vehicles/${a.vehicle_id}`)}
          />
        )}

        {data.priority_vehicles && data.priority_vehicles.length > 0 && (
          <PrioritySection
            vehicles={data.priority_vehicles}
            total={data.counters.total_vehicles}
            onSeeAll={() => navigate("/vehicles")}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- Fleet score hero ---------------- */
function FleetScoreHero({ score, counters, onDashboard }) {
  const level = score.level;
  const stripe =
    level === "green" ? "var(--mf-ok-solid)" :
    level === "orange" ? "var(--mf-upcoming-solid)" :
    "var(--mf-urgent-solid)";
  return (
    <section
      data-testid="fleet-score-hero"
      style={{
        position: "relative", background: "var(--mf-bg-card)",
        borderRadius: 24, padding: "22px 22px 20px",
        boxShadow: "var(--mf-shadow)", overflow: "hidden",
      }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: stripe }} />
      <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <HealthScoreBadge score={score.score} level={score.level} size={96} testID="fleet-score-badge" />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "var(--mf-text-muted)", letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 4 }}>
            Score de flotte
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.4, lineHeight: 1.2 }}>
            {score.message}
          </div>
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 14, fontSize: 13, color: "var(--mf-text-secondary)" }}>
            <MiniStat label="Véhicules" value={counters.total_vehicles} tint="var(--mf-brand)" icon={<Car size={14} />} />
            <MiniStat label="Urgent" value={counters.urgent_alerts} tint="var(--mf-urgent-solid)" icon={<AlertCircle size={14} />} />
            <MiniStat label="À prévoir" value={counters.upcoming_alerts} tint="var(--mf-upcoming-solid)" icon={<Clock size={14} />} />
          </div>
        </div>
        <button
          onClick={onDashboard}
          data-testid="fleet-score-analytics"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "10px 14px", borderRadius: 999,
            background: "var(--mf-bg-muted)", border: "none", color: "var(--mf-text)",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          <TrendingUp size={14} color="var(--mf-brand)" />
          Analytique
        </button>
      </div>
      {score.fallback && (
        <div style={{
          marginTop: 12, fontSize: 12, color: "var(--mf-text-muted)",
          fontStyle: "italic",
        }}>
          Score simplifié — le moteur intelligent est désactivé.
        </div>
      )}
    </section>
  );
}

function MiniStat({ label, value, tint, icon }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, color: "var(--mf-text-secondary)" }}>
      <span style={{
        width: 22, height: 22, borderRadius: 6,
        background: `${tint}22`, color: tint,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</span>
      <span style={{ color: "var(--mf-text)" }}>{value}</span>
      <span>{label.toLowerCase()}</span>
    </span>
  );
}

/* ---------------- Urgent alerts ---------------- */
function UrgentSection({ alerts, total, onSeeAll, onAlert }) {
  const showAll = total > alerts.length;
  return (
    <section data-testid="urgent-section">
      <SectionTitle
        icon={<AlertCircle size={14} color="var(--mf-urgent-solid)" />}
        right={showAll ? (
          <SeeAllButton onClick={onSeeAll} label="Toutes les alertes" testID="urgent-see-all" />
        ) : null}
      >
        Urgent · {total}
      </SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {alerts.map((a, i) => (
          <UrgentRow key={`${a.vehicle_id}-${a.type}-${i}`} alert={a} onClick={() => onAlert(a)} />
        ))}
      </div>
    </section>
  );
}

function UrgentRow({ alert, onClick }) {
  const c = statusColors("urgent");
  const label =
    alert.type === "custom_maintenance" || alert.type === "autre_document"
      ? alert.label || RULES_LABELS[alert.type] || "Entretien"
      : RULES_LABELS[alert.type] || alert.label || "Alerte";

  let overdueBadge = null;
  if (typeof alert.days_over === "number" && alert.days_over > 0) {
    overdueBadge = `Retard ${alert.days_over} j`;
  } else if (typeof alert.km_over === "number" && alert.km_over > 0) {
    overdueBadge = `+${alert.km_over.toLocaleString("fr-FR")} km`;
  }

  const priorityDot = alert.priority === "critical" ? "#7f1d1d" : c.solid;

  return (
    <button
      onClick={onClick}
      data-testid={`urgent-row-${alert.vehicle_id}-${alert.type}`}
      className="mf-anim-in"
      style={{
        width: "100%", background: "var(--mf-bg-card)", border: "none",
        borderRadius: 16, padding: 14, boxShadow: "var(--mf-shadow)",
        display: "flex", alignItems: "center", gap: 14, cursor: "pointer",
        textAlign: "left", overflow: "hidden", position: "relative",
      }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: priorityDot }} />
      <div style={{ flex: 1, marginLeft: 6, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4, alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)" }}>{label}</span>
          {overdueBadge && (
            <span style={{
              padding: "3px 10px", borderRadius: 999,
              background: c.bg, color: c.text,
              fontSize: 11, fontWeight: 800, letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}>
              {overdueBadge}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "var(--mf-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {alert.vehicle_label}{alert.vehicle_plate ? ` · ${alert.vehicle_plate}` : ""}
        </div>
        {alert.next_date && (
          <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
            Échéance : {formatDate(alert.next_date)}
          </div>
        )}
      </div>
      <ChevronRight size={16} color="var(--mf-text-muted)" />
    </button>
  );
}

/* ---------------- Priority vehicles ---------------- */
function PrioritySection({ vehicles, total, onSeeAll }) {
  return (
    <section data-testid="priority-section">
      <SectionTitle
        icon={<Car size={14} color="var(--mf-brand)" />}
        right={total > vehicles.length ? (
          <SeeAllButton onClick={onSeeAll} label={`Tous (${total})`} testID="priority-see-all" />
        ) : null}
      >
        Véhicules prioritaires
      </SectionTitle>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 14,
      }} data-testid="priority-vehicles-grid">
        {vehicles.map((v) => (
          <VehicleCard key={v.id} vehicle={v} testID={`home-priority-vehicle-${v.id}`} />
        ))}
      </div>
    </section>
  );
}

/* ---------------- Small primitives ---------------- */
function SectionTitle({ icon, children, right }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 10, gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {icon}
        <h3 style={{
          margin: 0, fontSize: 13, fontWeight: 800,
          color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.6,
        }}>
          {children}
        </h3>
      </div>
      {right}
    </div>
  );
}

function SeeAllButton({ onClick, label, testID }) {
  return (
    <button
      onClick={onClick}
      data-testid={testID}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "transparent", border: "none", padding: 0,
        color: "var(--mf-brand)", fontWeight: 700, fontSize: 12, cursor: "pointer",
      }}
    >
      {label}
      <ChevronRight size={14} />
    </button>
  );
}
